// frontend/src/components/header/index.tsx
import { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAudio, useAuth, useConfig } from '@chainlit/react-client';

import { isAdminConsole } from '@/AppMode';
import AudioPresence from '@/components/AudioPresence';
import ButtonLink from '@/components/ButtonLink';
import { useSidebar } from '@/components/ui/sidebar';

import AdminNav from './AdminNav';
import ApiKeys from './ApiKeys';
import ChatProfiles from './ChatProfiles';
import NewChatButton from './NewChat';
import ReadmeButton from './Readme';
import ShareButton from './Share';
import SidebarTrigger from './SidebarTrigger';
import { ThemeToggle } from './ThemeToggle';
import UserNav from './UserNav';

const Header = memo(() => {
  const { audioConnection } = useAudio();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useAuth();
  const { config } = useConfig();
  const { open, openMobile, isMobile } = useSidebar();

  const sidebarOpen = isMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;

  const links = config?.ui?.header_links || [];
  
  // Détecter si on est en mode admin
  const adminConsole = isAdminConsole();
  const isAdminRoute =
    adminConsole || location.pathname.startsWith('/admin') || location.pathname.startsWith('/users');

  return (
    <div
      className="p-3 flex h-[60px] items-center justify-between gap-2 relative"
      id="header"
    >
      <div className="flex items-center">
        {/* Back-office : ni historique des discussions ni nouveau chat */}
        {!isAdminRoute && historyEnabled && !sidebarOpen ? <SidebarTrigger /> : null}
        {!isAdminRoute && (!historyEnabled || !sidebarOpen) ? (
          <NewChatButton navigate={navigate} />
        ) : null}

        {/* Masquer ChatProfiles en mode admin */}
        {!isAdminRoute && <ChatProfiles navigate={navigate} />}
        
        {/* 🆕 Navigation Admin */}
        <AdminNav />
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {audioConnection === 'on' ? (
          <AudioPresence
            type="server"
            height={35}
            width={70}
            barCount={4}
            barSpacing={2}
          />
        ) : null}
      </div>

      <div />
      <div className="flex items-center gap-1">
        {/* Console master : rien qui relève du chat */}
        {!adminConsole && <ShareButton />}
        {!adminConsole && <ReadmeButton />}
        {!adminConsole && <ApiKeys />}
        {!adminConsole && links &&
          links.map((link, index) => (
            <ButtonLink
              key={`${link.name}-${link.url}-${index}`}
              name={link.name}
              displayName={link.display_name}
              iconUrl={link.icon_url}
              url={link.url}
              target={link.target}
            />
          ))}
        <ThemeToggle />
        <UserNav />
      </div>
    </div>
  );
});

export { Header };